import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface IChatBotOverlayContextValue {
  isProtectedOverlayActive: boolean;
  registerProtectedOverlay: (id: string) => void;
  unregisterProtectedOverlay: (id: string) => void;
}

interface IOverlayOffsetProviderProps {
  children: ReactNode;
}

interface IUseChatBotOverlayRegistrationParams {
  id: string;
  isOpen?: boolean;
}

const OverlayOffsetContext = createContext<IChatBotOverlayContextValue>({
  isProtectedOverlayActive: false,
  registerProtectedOverlay: () => null,
  unregisterProtectedOverlay: () => null,
});

export const OverlayOffsetProvider = ({ children }: IOverlayOffsetProviderProps): JSX.Element => {
  const protectedOverlaysRef = useRef<Set<string>>(new Set());
  const [isProtectedOverlayActive, setIsProtectedOverlayActive] = useState(false);

  const syncProtectedOverlayState = useCallback((): void => {
    setIsProtectedOverlayActive(protectedOverlaysRef.current.size > 0);
  }, []);

  const registerProtectedOverlay = useCallback(
    (id: string): void => {
      protectedOverlaysRef.current.add(id);
      syncProtectedOverlayState();
    },
    [syncProtectedOverlayState],
  );

  const unregisterProtectedOverlay = useCallback(
    (id: string): void => {
      protectedOverlaysRef.current.delete(id);
      syncProtectedOverlayState();
    },
    [syncProtectedOverlayState],
  );

  const value = useMemo(
    () => ({
      isProtectedOverlayActive,
      registerProtectedOverlay,
      unregisterProtectedOverlay,
    }),
    [isProtectedOverlayActive, registerProtectedOverlay, unregisterProtectedOverlay],
  );

  return <OverlayOffsetContext.Provider value={value}>{children}</OverlayOffsetContext.Provider>;
};

export const useChatBotOverlay = (): IChatBotOverlayContextValue => useContext(OverlayOffsetContext);

export const useChatBotOverlayRegistration = ({ id, isOpen }: IUseChatBotOverlayRegistrationParams): void => {
  const { registerProtectedOverlay, unregisterProtectedOverlay } = useChatBotOverlay();

  useEffect(() => {
    if (!isOpen) {
      unregisterProtectedOverlay(id);

      return undefined;
    }

    registerProtectedOverlay(id);

    return () => unregisterProtectedOverlay(id);
  }, [id, isOpen, registerProtectedOverlay, unregisterProtectedOverlay]);
};
